import { useState, type SyntheticEvent } from "react";
import { t } from "i18next";
import classNames from "classnames";
import { ClipMoreMenu } from "@components/ClipMoreMenu/ClipMoreMenu";
import type { UserAssetTrashItemRecord } from "@apis/http/api";

type Composition = "portrait" | "landscape";

interface TrashImageCardProps {
  record: UserAssetTrashItemRecord;
  onPreview: (src: string) => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
}

export const TrashImageCard = ({ record, onPreview, onRestore, onDelete }: TrashImageCardProps) => {
  const src = record.url || "";
  const [composition, setComposition] = useState<Composition>("portrait");
  const canPreview = !!src;
  const id = String(record.id);

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    setComposition(naturalWidth > naturalHeight ? "landscape" : "portrait");
  };

  const handleClick = () => {
    if (!canPreview) return;
    onPreview(src);
  };

  return (
    <ClipMoreMenu
      className="kalo-white-card w-full h-full aspect-3/4 overflow-hidden bg-kds-dark"
      options={[
        {
          key: "restore",
          label: t("Restore"),
          onClick: () => onRestore(id),
        },
        {
          key: "delete",
          label: t("Delete forever"),
          danger: true,
          onClick: () => onDelete(id),
        },
      ]}
    >
      <button
        type="button"
        disabled={!canPreview}
        className={classNames(
          "absolute inset-0 flex items-center justify-center border-0 bg-transparent p-0",
          canPreview ? "cursor-pointer" : "cursor-default",
        )}
        onClick={handleClick}
      >
        {src ? (
          <img
            src={src}
            alt=""
            className={classNames(
              "pointer-events-none h-full w-full",
              composition === "portrait" ? "object-cover" : "object-contain",
            )}
            onLoad={handleImageLoad}
          />
        ) : (
          <div className="h-full w-full bg-kds-bg-subtle" />
        )}
      </button>
    </ClipMoreMenu>
  );
};

export default TrashImageCard;
